/**
 * Bundled font loader for the text generator.
 *
 * Two fonts ship with the application — a sans-serif (Inter) and a serif
 * (Source Serif 4) — and both are loaded once when the generator worker
 * boots. After `ensureFontsReady()` resolves, `getFont()` is a sync lookup
 * a generator's `build()` can rely on every frame.
 *
 * The loader runs inside the generator worker; the main thread never
 * touches font data, so each tab carries one parsed copy of each font in
 * the worker's heap.
 */

import opentype, { type Font } from "opentype.js";
import interUrl from "./fonts/inter.ttf?url";
import serifUrl from "./fonts/source-serif.ttf?url";

export type FontFamily = "serif" | "sans-serif";

let _ready: Promise<void> | null = null;
const _fonts = new Map<FontFamily, Font>();

async function loadOne(url: string): Promise<Font> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch font (${response.status}): ${response.statusText}`,
    );
  }
  const buf = await response.arrayBuffer();
  return opentype.parse(buf);
}

/**
 * Kick off (or reuse) the one-shot font load. Subsequent callers receive
 * the same in-flight promise, so the worker boot path can `Promise.all`
 * this alongside the WASM init without duplicating fetches.
 *
 * Resolves when both fonts are parsed and registered. Rejects on the
 * first failure — generator builds will throw a clear error when they
 * try to read an unloaded font, surfacing the failure rather than
 * silently rendering nothing.
 */
export function ensureFontsReady(): Promise<void> {
  if (_ready) return _ready;
  _ready = (async () => {
    const [sans, serif] = await Promise.all([
      loadOne(interUrl),
      loadOne(serifUrl),
    ]);
    _fonts.set("sans-serif", sans);
    _fonts.set("serif", serif);
  })();
  return _ready;
}

/**
 * Return the parsed Font for the requested family. Throws if the font
 * has not been loaded yet — call `ensureFontsReady()` first (the worker
 * boot path already does so, so generator `build()` bodies can call
 * this directly).
 */
export function getFont(family: FontFamily): Font {
  const f = _fonts.get(family);
  if (!f) {
    throw new Error(
      `Font "${family}" is not loaded — ensureFontsReady() must resolve first.`,
    );
  }
  return f;
}

/**
 * Synchronously install a parsed font for the given family from raw
 * bytes. Mirrors the `ensureWasmReadyFromBytes` pattern in
 * `wasm/init.ts`: any environment that already holds the font bytes
 * (test setup files, embedded assets) can skip the fetch + parse round-
 * trip and seed the cache directly. Once a family is registered this
 * way, `ensureFontsReady()` resolves immediately rather than kicking
 * off a network fetch.
 */
export function registerFontFromBuffer(
  family: FontFamily,
  buffer: ArrayBuffer,
): void {
  _fonts.set(family, opentype.parse(buffer));
  if (_fonts.has("serif") && _fonts.has("sans-serif") && !_ready) {
    _ready = Promise.resolve();
  }
}
